import { ChevronDown } from 'lucide-react';
import { useId, useState } from 'react';

import { cn } from '@/shared/lib/cn';
import { matchesSearch } from '@/shared/lib/search';

import { FIELD_TRIGGER } from './control';
import { Input } from './Field';

export interface SearchSelectOption {
  value: string;
  label: string;
  /** Подробность справа от названия: доза, место хранения. */
  detail?: string;
  /** Что ещё находит этот вариант, кроме названия: действующее вещество. */
  keywords?: readonly string[];
}

interface SearchSelectProps {
  label: string;
  value: string | null;
  options: readonly SearchSelectOption[];
  /** Что написано в поле, пока ничего не выбрано: «Выберите лекарство». */
  placeholder: string;
  /** Подпись поиска: «Найти лекарство». */
  searchLabel: string;
  onChange: (value: string) => void;
}

/**
 * Выбор из длинного списка — по стрелке и с поиском.
 *
 * Для списков, которые кнопками не помещаются: вся аптечка, когда нужно
 * выбрать одно лекарство. Свёрнутое поле выглядит как обычное и показывает
 * выбранное; раскрытое — строка поиска и варианты под ней. Выбор сворачивает
 * список обратно.
 */
export function SearchSelect({
  label,
  value,
  options,
  placeholder,
  searchLabel,
  onChange,
}: SearchSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const panelId = useId();

  const chosen = options.find((option) => option.value === value) ?? null;
  const shown = options.filter((option) =>
    matchesSearch([option.label, option.detail, ...(option.keywords ?? [])], query),
  );

  const choose = (option: SearchSelectOption) => {
    onChange(option.value);
    setOpen(false);
    setQuery('');
  };

  return (
    <div className="space-y-1.5">
      <p className="text-[13px] font-medium text-muted">{label}</p>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((current) => !current)}
        className={cn('flex w-full items-center justify-between gap-2 text-left', FIELD_TRIGGER)}
      >
        <span
          className={cn(
            'min-w-0 flex-1 truncate text-[15px]',
            chosen ? 'text-content' : 'text-subtle',
          )}
        >
          <span className="sr-only">{label}: </span>
          {chosen?.label ?? placeholder}
        </span>
        <ChevronDown
          size={16}
          aria-hidden
          className={cn(
            'shrink-0 text-muted transition-transform duration-200',
            open && 'rotate-180',
          )}
        />
      </button>

      {open && (
        <div id={panelId} className="space-y-2 pt-1">
          <Input
            type="search"
            aria-label={searchLabel}
            placeholder={searchLabel}
            value={query}
            // Список открыли, чтобы найти: печатать можно сразу.
            autoFocus
            onChange={(event) => setQuery(event.target.value)}
          />

          {shown.length === 0 ? (
            <p className="px-1 text-[13px] text-subtle">Ничего не нашлось</p>
          ) : (
            <ul aria-label={`Варианты: ${label}`} className="max-h-72 space-y-1.5 overflow-y-auto">
              {shown.map((option) => (
                <li key={option.value}>
                  <button
                    type="button"
                    aria-pressed={option.value === value}
                    onClick={() => choose(option)}
                    className={cn(
                      'flex min-h-11 w-full items-center justify-between gap-3 rounded-md border px-4',
                      'text-left text-[15px] transition-colors',
                      option.value === value
                        ? 'border-brand bg-brand-soft text-brand-soft-text'
                        : 'border-border bg-surface-2 hover:bg-surface-3',
                    )}
                  >
                    <span className="truncate">{option.label}</span>
                    {option.detail && (
                      <span className="shrink-0 text-[13px] text-muted">{option.detail}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
